import { useState, useEffect, useCallback, useRef } from "react";

export interface DeviceFingerprint {
  ip_address: string | null;
  user_agent: string;
  platform: string;
  language: string;
  timezone: string;
  screen_resolution: string;
  color_depth: number;
  hardware_concurrency: number;
  device_memory: number | null;
  touch_support: boolean;
  webgl_vendor: string | null;
  webgl_renderer: string | null;
  canvas_fingerprint: string | null;
  timestamp: string;
  // NEW: Bot detection signals
  is_headless: boolean;
  is_automated: boolean;
  has_webdriver: boolean;
  plugins_count: number;
  languages_count: number;
  screen_depth_ratio: number;
  timing_anomaly: boolean;
  // NEW: Behavioral signals
  mouse_movements: number;
  keyboard_events: number;
  scroll_events: number;
  session_duration_ms: number;
}

// Detect headless browsers and automation tools
function detectHeadlessBrowser(): { isHeadless: boolean; isAutomated: boolean; hasWebdriver: boolean } {
  let isHeadless = false;
  let isAutomated = false;
  let hasWebdriver = false;

  try {
    // Check for webdriver (Selenium, Puppeteer, Playwright)
    hasWebdriver = !!(
      (navigator as any).webdriver ||
      (window as any).__webdriver_script_fn ||
      (window as any).__driver_evaluate ||
      (window as any).__webdriver_evaluate ||
      (window as any).__selenium_evaluate ||
      (window as any).__fxdriver_evaluate ||
      (window as any).__driver_unwrapped ||
      (window as any).__webdriver_unwrapped ||
      (window as any).__selenium_unwrapped ||
      (window as any).__fxdriver_unwrapped ||
      (window as any).__webdriver_script_func ||
      document.documentElement.getAttribute('webdriver') ||
      document.documentElement.getAttribute('selenium') ||
      document.documentElement.getAttribute('driver')
    );

    // Check for PhantomJS
    if ((window as any).callPhantom || (window as any)._phantom) {
      isHeadless = true;
      isAutomated = true;
    }

    // Check for headless Chrome indicators
    if (navigator.userAgent.includes('HeadlessChrome')) {
      isHeadless = true;
    }

    // Check for missing plugins (bots often have 0 plugins)
    if (navigator.plugins && navigator.plugins.length === 0 && !('ontouchstart' in window)) {
      isHeadless = true;
    }

    // Check for missing languages
    if (!navigator.languages || navigator.languages.length === 0) {
      isHeadless = true;
    }

    // Check for Puppeteer/Playwright specific
    if ((window as any).__playwright || (window as any).__puppeteer) {
      isAutomated = true;
      isHeadless = true;
    }

    // Check for automation flags in Chrome
    if ((navigator as any).permissions) {
      (navigator as any).permissions.query({ name: 'notifications' }).then((result: any) => {
        if (result.state === 'denied' && Notification.permission === 'default') {
          isHeadless = true;
        }
      }).catch(() => {});
    }

    // Check outerWidth/innerWidth ratio (headless browsers often have 0 difference)
    if (window.outerWidth === 0 || window.outerHeight === 0) {
      isHeadless = true;
    }

    // Check for chrome.runtime in non-extension context
    if ((window as any).chrome && !(window as any).chrome.runtime) {
      // This could be a sign of automation but not conclusive
    }

    // Check for missing chrome object in Chrome browser
    const isChrome = navigator.userAgent.includes('Chrome');
    if (isChrome && !(window as any).chrome) {
      isHeadless = true;
    }

  } catch (e) {
    console.warn("Bot detection error:", e);
  }

  // If webdriver detected, it's definitely automated
  if (hasWebdriver) {
    isAutomated = true;
  }

  return { isHeadless, isAutomated, hasWebdriver };
}

// Detect timing anomalies (kept for compatibility; avoid false positives)
function detectTimingAnomaly(): boolean {
  // The previous implementation flagged users simply because the app was freshly loaded.
  // That caused unnecessary risk points on legitimate sessions.
  return false;
}

// Generate a simple canvas fingerprint
function getCanvasFingerprint(): string | null {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    
    canvas.width = 200;
    canvas.height = 50;
    
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("FLOW NFT", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("fingerprint", 4, 17);
    
    return canvas.toDataURL().slice(-50);
  } catch {
    return null;
  }
}

// Get WebGL info
function getWebGLInfo(): { vendor: string | null; renderer: string | null } {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return { vendor: null, renderer: null };
    
    const debugInfo = (gl as WebGLRenderingContext).getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) return { vendor: null, renderer: null };
    
    return {
      vendor: (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
      renderer: (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
    };
  } catch {
    return { vendor: null, renderer: null };
  }
}

// Fetch IP address from a free API
async function fetchIPAddress(): Promise<string | null> {
  try {
    const services = [
      "https://api.ipify.org?format=json",
      "https://api64.ipify.org?format=json",
    ];
    
    for (const service of services) {
      try {
        const response = await fetch(service, { 
          method: "GET",
          cache: "no-store",
          signal: AbortSignal.timeout(5000)
        });
        if (response.ok) {
          const data = await response.json();
          return data.ip || null;
        }
      } catch {
        continue;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function useDeviceFingerprint() {
  const [fingerprint, setFingerprint] = useState<DeviceFingerprint | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Behavioral tracking
  const mouseMovements = useRef(0);
  const keyboardEvents = useRef(0);
  const scrollEvents = useRef(0);
  const sessionStart = useRef(Date.now());

  // Track user interactions for bot detection
  useEffect(() => {
    const handleMouseMove = () => { mouseMovements.current++; };
    const handleKeyDown = () => { keyboardEvents.current++; };
    const handleScroll = () => { scrollEvents.current++; };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('keydown', handleKeyDown, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const collectFingerprint = useCallback(async (): Promise<DeviceFingerprint> => {
    const webglInfo = getWebGLInfo();
    const ip = await fetchIPAddress();
    const botDetection = detectHeadlessBrowser();
    const timingAnomaly = detectTimingAnomaly();
    
    const fp: DeviceFingerprint = {
      ip_address: ip,
      user_agent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_resolution: `${screen.width}x${screen.height}`,
      color_depth: screen.colorDepth,
      hardware_concurrency: navigator.hardwareConcurrency || 0,
      device_memory: (navigator as any).deviceMemory || null,
      touch_support: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      webgl_vendor: webglInfo.vendor,
      webgl_renderer: webglInfo.renderer,
      canvas_fingerprint: getCanvasFingerprint(),
      timestamp: new Date().toISOString(),
      // Bot detection signals
      is_headless: botDetection.isHeadless,
      is_automated: botDetection.isAutomated,
      has_webdriver: botDetection.hasWebdriver,
      plugins_count: navigator.plugins?.length || 0,
      languages_count: navigator.languages?.length || 0,
      screen_depth_ratio: window.outerWidth > 0 ? window.innerWidth / window.outerWidth : 0,
      timing_anomaly: timingAnomaly,
      // Behavioral signals
      mouse_movements: mouseMovements.current,
      keyboard_events: keyboardEvents.current,
      scroll_events: scrollEvents.current,
      session_duration_ms: Date.now() - sessionStart.current,
    };
    
    return fp;
  }, []);

  useEffect(() => {
    collectFingerprint().then((fp) => {
      setFingerprint(fp);
      setLoading(false);
    });
  }, [collectFingerprint]);

  // Get a simplified version for logging
  const getComplianceData = useCallback(() => {
    if (!fingerprint) return null;
    
    return {
      ip: fingerprint.ip_address,
      ua: fingerprint.user_agent,
      platform: fingerprint.platform,
      lang: fingerprint.language,
      tz: fingerprint.timezone,
      screen: fingerprint.screen_resolution,
      hw: fingerprint.hardware_concurrency,
      touch: fingerprint.touch_support,
      gpu: fingerprint.webgl_renderer,
      ts: fingerprint.timestamp,
      bot_signals: {
        headless: fingerprint.is_headless,
        automated: fingerprint.is_automated,
        webdriver: fingerprint.has_webdriver,
      }
    };
  }, [fingerprint]);

  // Get bot risk score (0-100)
  const getBotRiskScore = useCallback((): number => {
    if (!fingerprint) return 0;
    
    let score = 0;
    
    // Critical indicators (instant high score)
    if (fingerprint.has_webdriver) score += 80;
    if (fingerprint.is_automated) score += 70;
    if (fingerprint.is_headless) score += 50;
    
    // Suspicious indicators
    if (fingerprint.plugins_count === 0 && !fingerprint.touch_support) score += 20;
    if (fingerprint.languages_count === 0) score += 15;
    if (fingerprint.screen_depth_ratio === 0) score += 15;
    if (fingerprint.timing_anomaly) score += 25;
    
    // Behavioral analysis (bots don't move mouse or scroll)
    if (fingerprint.session_duration_ms > 5000) {
      if (fingerprint.mouse_movements === 0) score += 30;
      if (fingerprint.scroll_events === 0) score += 15;
    }
    
    // Missing WebGL (common in headless)
    if (!fingerprint.webgl_renderer) score += 10;
    
    return Math.min(score, 100);
  }, [fingerprint]);

  return { 
    fingerprint, 
    loading, 
    getComplianceData,
    getBotRiskScore,
    refresh: collectFingerprint 
  };
}